import type { Icon } from "@phosphor-icons/react";
import { CaretRight, Check } from "@phosphor-icons/react";
import { ContextMenu, DropdownMenu } from "radix-ui";
import type { ReactElement, ReactNode } from "react";
import { useNavigateTo } from "~/foundation/components/navigation";
import { depths } from "~/foundation/styles/depths";
import { cn } from "~/foundation/utils/cn";

/**
 * The rendering contract between the action system and the UI. Adapters produce these;
 * this file is the only place that decides how they look.
 */
export type MenuItem =
  | {
      type: "button";
      title: string;
      icon?: Icon;
      dangerous?: boolean;
      disabled?: boolean;
      selected?: boolean;
      onClick: () => void;
    }
  | { type: "route"; title: string; icon?: Icon; to: string }
  | { type: "submenu"; title: string; icon?: Icon; items: MenuItem[] }
  | { type: "separator" }
  | { type: "heading"; title: string };

const ITEM_CLASS =
  "flex h-8 cursor-pointer select-none items-center gap-2.5 rounded-sm px-3 text-body " +
  "text-text outline-none data-[highlighted]:bg-menu-item-selected " +
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

const CONTENT_CLASS =
  "slate-pop min-w-[200px] max-w-[320px] rounded-md border border-divider bg-menu-bg p-1 shadow-menu " +
  // Radix positions but does not constrain: without these a long menu (or submenu) runs off
  // the bottom of the viewport and the rest is unreachable.
  "max-h-[min(70vh,var(--radix-popper-available-height,70vh))] overflow-y-auto scrollbar-thin " +
  "overscroll-contain";

/**
 * Radix's dropdown and context menus share an API; rendering both from one function is what
 * guarantees an overflow menu and a right-click menu can never drift apart.
 */
interface MenuParts {
  Portal: typeof DropdownMenu.Portal;
  Item: typeof DropdownMenu.Item;
  Separator: typeof DropdownMenu.Separator;
  Label: typeof DropdownMenu.Label;
  Sub: typeof DropdownMenu.Sub;
  SubTrigger: typeof DropdownMenu.SubTrigger;
  SubContent: typeof DropdownMenu.SubContent;
}

const dropdownParts: MenuParts = {
  Portal: DropdownMenu.Portal,
  Item: DropdownMenu.Item,
  Separator: DropdownMenu.Separator,
  Label: DropdownMenu.Label,
  Sub: DropdownMenu.Sub,
  SubTrigger: DropdownMenu.SubTrigger,
  SubContent: DropdownMenu.SubContent,
};

const contextParts = {
  Portal: ContextMenu.Portal,
  Item: ContextMenu.Item,
  Separator: ContextMenu.Separator,
  Label: ContextMenu.Label,
  Sub: ContextMenu.Sub,
  SubTrigger: ContextMenu.SubTrigger,
  SubContent: ContextMenu.SubContent,
} as unknown as MenuParts;

function ItemIcon({ icon: IconComponent }: { icon?: Icon }) {
  if (!IconComponent) {
    return null;
  }
  return <IconComponent size={16} aria-hidden className="shrink-0 text-text-tertiary" />;
}

function MenuItems({ items, parts }: { items: MenuItem[]; parts: MenuParts }) {
  const navigate = useNavigateTo();
  const { Portal, Item, Separator, Label, Sub, SubTrigger, SubContent } = parts;

  const render = (list: MenuItem[]): ReactNode =>
    list.map((item, index) => {
      switch (item.type) {
        case "separator":
          // biome-ignore lint/suspicious/noArrayIndexKey: a separator has no identity
          return <Separator key={`sep-${index}`} className="my-1 h-px bg-divider" />;

        case "heading":
          return (
            <Label
              key={`heading-${item.title}`}
              className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-text-tertiary"
            >
              {item.title}
            </Label>
          );

        case "submenu":
          return (
            <Sub key={`sub-${item.title}`}>
              <SubTrigger className={ITEM_CLASS}>
                <ItemIcon icon={item.icon} />
                <span className="flex-1 truncate">{item.title}</span>
                <CaretRight size={12} aria-hidden className="text-text-tertiary rtl:rotate-180" />
              </SubTrigger>
              <Portal>
                <SubContent
                  className={CONTENT_CLASS}
                  sideOffset={4}
                  style={{ zIndex: depths.menu }}
                >
                  {render(item.items)}
                </SubContent>
              </Portal>
            </Sub>
          );

        case "route":
          return (
            <Item key={`route-${item.to}`} className={ITEM_CLASS} asChild>
              <a
                href={item.to}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey) {
                    return;
                  }
                  event.preventDefault();
                  navigate(item.to);
                }}
              >
                <ItemIcon icon={item.icon} />
                <span className="flex-1 truncate">{item.title}</span>
              </a>
            </Item>
          );

        default:
          return (
            <Item
              key={`button-${item.title}`}
              className={cn(ITEM_CLASS, item.dangerous && "text-danger")}
              disabled={item.disabled}
              onSelect={item.onClick}
            >
              <ItemIcon icon={item.icon} />
              <span className="flex-1 truncate">{item.title}</span>
              {item.selected ? <Check size={14} aria-hidden className="text-accent" /> : null}
            </Item>
          );
      }
    });

  return <>{render(items)}</>;
}

export interface MenuProps {
  items: MenuItem[];
  trigger: ReactElement;
  /** Logical alignment — Radix resolves start/end against the document direction. */
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  ariaLabel?: string;
}

export function Menu({
  items,
  trigger,
  align = "start",
  side = "bottom",
  open,
  onOpenChange,
  ariaLabel,
}: MenuProps) {
  return (
    <DropdownMenu.Root
      {...(open === undefined ? {} : { open })}
      {...(onOpenChange ? { onOpenChange } : {})}
    >
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          aria-label={ariaLabel}
          className={CONTENT_CLASS}
          style={{ zIndex: depths.menu }}
        >
          <MenuItems items={items} parts={dropdownParts} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Right-click surface built from the same MenuItem[]. */
export function ContextMenuSurface({
  items,
  children,
  ariaLabel,
}: {
  items: MenuItem[];
  children: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content
          collisionPadding={8}
          aria-label={ariaLabel}
          className={CONTENT_CLASS}
          style={{ zIndex: depths.menu }}
        >
          <MenuItems items={items} parts={contextParts} />
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
